import { useEffect, useState } from 'react';
import { CheckCircle2, Copy, Loader2, MessageCircle, Plus, RefreshCw, Settings, Trash2, XCircle } from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { DOMINIO_ENVIO, IDIOMAS, NOMBRE_IDIOMA, type AjustesComunicaciones } from '@/lib/comunicaciones';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * Remitentes, respuesta por defecto y ritmo de envío de las campañas.
 */
export default function AjustesPage() {
  const { data, loading, error } = useApi(() => api.getAjustesComunicaciones(), []);
  const [a, setA] = useState<AjustesComunicaciones | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState<{ ok: boolean; texto: string } | null>(null);

  useEffect(() => { if (data) setA(data); }, [data]);

  if (loading && !a) return <LoadingSpinner />;
  if (error && !a) return <div className="card text-sm text-red-600">{error}</div>;
  if (!a) return null;

  const guardar = async () => {
    setGuardando(true);
    setMensaje(null);
    try {
      setA(await api.guardarAjustesComunicaciones(a));
      setMensaje({ ok: true, texto: 'Guardado' });
    } catch (err) {
      setMensaje({ ok: false, texto: err instanceof Error ? err.message : 'No se pudo guardar' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-2">
        <Settings size={24} className="text-sereno-dark" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Ajustes de comunicaciones</h1>
          <p className="text-sm text-sereno-dark/40 mt-0.5">Desde dónde salen las campañas y a qué ritmo.</p>
        </div>
      </div>

      <div className="card space-y-4">
        <div>
          <div className="font-semibold text-sereno-dark">Remitentes</div>
          <p className="text-sm text-sereno-dark/50 mt-0.5">
            Direcciones @{DOMINIO_ENVIO} que se pueden elegir en una campaña. Cada una tiene que existir también en Azure
            (dominio {DOMINIO_ENVIO} del servicio <code>sereno-email</code>), y el nombre que ve el destinatario es el que tiene allí:
            el de aquí solo sirve para reconocerla en el CRM.
          </p>
        </div>
        {a.remitentes.map((r, i) => (
          <div key={i} className="flex gap-2 items-start">
            {/* En el móvil dirección y nombre van uno debajo del otro: en fila la dirección no se lee */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row gap-2">
              <input className="input flex-1 min-w-0" value={r.direccion} placeholder={`hola@${DOMINIO_ENVIO}`}
                onChange={e => setA({ ...a, remitentes: a.remitentes.map((x, j) => (j === i ? { ...x, direccion: e.target.value } : x)) })} />
              <input className="input w-full sm:w-48" value={r.nombre} placeholder="Nombre"
                onChange={e => setA({ ...a, remitentes: a.remitentes.map((x, j) => (j === i ? { ...x, nombre: e.target.value } : x)) })} />
            </div>
            <button type="button" disabled={a.remitentes.length === 1} className="p-2 text-sereno-dark/30 hover:text-red-600 disabled:opacity-30" title="Quitar"
              onClick={() => setA({ ...a, remitentes: a.remitentes.filter((_, j) => j !== i) })}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button type="button" className="btn-ghost border border-gray-200 text-sm flex items-center gap-1.5"
          onClick={() => setA({ ...a, remitentes: [...a.remitentes, { direccion: `@${DOMINIO_ENVIO}`, nombre: 'Sereno' }] })}>
          <Plus size={14} /> Añadir remitente
        </button>
        <div>
          <label className="label">Las respuestas van a (por defecto)</label>
          <input className="input" type="email" value={a.responderAPorDefecto} onChange={e => setA({ ...a, responderAPorDefecto: e.target.value })} />
        </div>
      </div>

      <div className="card space-y-4">
        <div>
          <div className="font-semibold text-sereno-dark">Ritmo de envío</div>
          <p className="text-sm text-sereno-dark/50 mt-0.5">
            Azure da a la suscripción 30 correos por minuto y 100 por hora mientras no se pida más, y esa cuota la comparten las
            campañas con otros correos. Con los valores de partida queda margen. Cuando el dominio lleve unas semanas enviando
            sin rebotes, se puede pedir a Azure una cuota mayor y subir esto.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Por minuto</label>
            <input className="input" type="number" min={1} max={500} value={a.porMinuto} onChange={e => setA({ ...a, porMinuto: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Por hora</label>
            <input className="input" type="number" min={1} max={20000} value={a.porHora} onChange={e => setA({ ...a, porHora: Number(e.target.value) })} />
          </div>
        </div>
      </div>

      <div className="card space-y-4">
        <div>
          <div className="font-semibold text-sereno-dark flex items-center gap-2"><MessageCircle size={16} /> WhatsApp</div>
          <p className="text-sm text-sereno-dark/50 mt-0.5">
            La cuenta de WhatsApp Business de Sereno en Meta, la misma a la que está conectado Hostify. El CRM entra con la app
            «Sereno» y el usuario del sistema «crm-sereno»; su token va en el Key Vault (WhatsApp-Token), no aquí.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {([['wabaId', 'ID de la cuenta de WhatsApp Business'], ['phoneNumberId', 'ID del número de teléfono'], ['appId', 'ID de la app']] as const).map(([k, etiqueta]) => (
            <div key={k}>
              <label className="label">{etiqueta}</label>
              <input className="input font-mono text-sm" inputMode="numeric" value={a.whatsapp[k]}
                onChange={e => setA({ ...a, whatsapp: { ...a.whatsapp, [k]: e.target.value.replace(/[^\d]/g, '') } })} />
            </div>
          ))}
        </div>
        <div>
          <div className="label">Respuesta automática a quien conteste una campaña</div>
          <p className="text-xs text-sereno-dark/40 mb-2">Una vez cada 24 h como mucho, y no si alguien del equipo le ha contestado en ese tiempo. Vacío = sin respuesta automática en ese idioma.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {IDIOMAS.map(i => (
              <textarea key={i} className="input text-sm" rows={2} placeholder={NOMBRE_IDIOMA[i]} value={a.whatsapp.respuestaAutomatica[i] || ''}
                onChange={e => setA({ ...a, whatsapp: { ...a.whatsapp, respuestaAutomatica: { ...a.whatsapp.respuestaAutomatica, [i]: e.target.value } } })} />
            ))}
          </div>
        </div>
      </div>

      <EstadoWhatsApp />

      <div className="flex items-center justify-end gap-3">
        {mensaje && (
          <span className={mensaje.ok ? 'text-sm text-emerald-700 flex items-center gap-1' : 'text-sm text-red-600'}>
            {mensaje.ok && <CheckCircle2 size={14} />} {mensaje.texto}
          </span>
        )}
        <button type="button" onClick={guardar} disabled={guardando} className="btn-primary text-sm flex items-center gap-1.5">
          {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
        </button>
      </div>
    </div>
  );
}

/**
 * Si WhatsApp está bien conectado, y lo que hay que copiar en Meta para el
 * webhook. Se lee aparte de los ajustes: pregunta a Meta y al Key Vault.
 */
function EstadoWhatsApp() {
  const { data, loading, error, refresh } = useApi(() => api.getEstadoWhatsApp(), []);
  const [suscribiendo, setSuscribiendo] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const copiar = (t: string) => { void navigator.clipboard?.writeText(t); setAviso('Copiado'); setTimeout(() => setAviso(null), 1500); };
  const suscribir = async () => {
    setSuscribiendo(true);
    try {
      await api.suscribirWhatsApp();
      setAviso('App suscrita a la cuenta: Meta ya le manda los avisos');
    } catch (err) {
      setAviso(err instanceof Error ? err.message : 'No se pudo');
    } finally {
      setSuscribiendo(false);
    }
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sereno-dark">Estado de la conexión con WhatsApp</div>
        <button type="button" onClick={refresh} className="btn-ghost text-xs flex items-center gap-1.5 px-2 py-1">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Comprobar
        </button>
      </div>
      {error && <div className="text-sm text-red-600">{error}</div>}
      {data && (
        <>
          <ul className="text-sm space-y-1">
            <Comprobacion ok={!!(data.ids.wabaId && data.ids.phoneNumberId)} texto="IDs de la cuenta y del número guardados (arriba)" />
            <Comprobacion ok={data.token} texto="Token en el Key Vault (WhatsApp-Token)" />
            <Comprobacion ok={data.appSecret} texto="Clave secreta de la app, para la firma de los avisos (la misma que los leads de Facebook)" />
            <Comprobacion ok={!!data.numero} texto={data.numero
              ? `Número: ${data.numero.verified_name} · ${data.numero.display_phone_number} · calidad ${(data.numero.quality_rating || '—').toLowerCase()} · límite ${data.numero.messaging_limit_tier || '—'}`
              : `Número: ${data.errorNumero || 'sin comprobar'}`} />
            <Comprobacion ok={!!data.ultimoAvisoEn} texto={data.ultimoAvisoEn ? `Último aviso de Meta: ${formatDateTime(data.ultimoAvisoEn)}` : 'Todavía no ha llegado ningún aviso de Meta desde el último arranque'} />
          </ul>
          <div className="border-t border-gray-100 pt-3 space-y-2 text-sm">
            <div className="text-sereno-dark/70">En Meta → tu app → WhatsApp → Configuración → <strong>Webhook</strong>:</div>
            <Copiable etiqueta="URL de devolución de llamada" valor={data.webhook.url} onCopiar={copiar} />
            <Copiable etiqueta="Token de verificación" valor={data.webhook.tokenVerificacion} onCopiar={copiar} />
            <div className="text-xs text-sereno-dark/50">Y en «Campos del webhook», suscríbete a <code>messages</code>. Después, pulsa aquí para que Meta mande los avisos de la cuenta a esta app:</div>
            <button type="button" onClick={suscribir} disabled={suscribiendo || !data.token} className="btn-ghost border border-gray-200 text-xs flex items-center gap-1.5 px-3 py-1.5">
              {suscribiendo && <Loader2 size={13} className="animate-spin" />} Suscribir la app a la cuenta
            </button>
            {aviso && <div className="text-xs text-sereno-dark/60">{aviso}</div>}
          </div>
        </>
      )}
    </div>
  );
}

function Comprobacion({ ok, texto }: { ok: boolean; texto: string }) {
  return (
    <li className="flex items-start gap-2">
      {ok ? <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> : <XCircle size={15} className="text-sereno-dark/30 mt-0.5 shrink-0" />}
      <span className={ok ? 'text-sereno-dark/80' : 'text-sereno-dark/50'}>{texto}</span>
    </li>
  );
}

function Copiable({ etiqueta, valor, onCopiar }: { etiqueta: string; valor: string; onCopiar: (v: string) => void }) {
  return (
    <div>
      <div className="text-xs text-sereno-dark/40">{etiqueta}</div>
      <div className="flex gap-2">
        <code className="input text-xs flex-1 truncate py-1.5">{valor}</code>
        <button type="button" onClick={() => onCopiar(valor)} className="btn-ghost border border-gray-200 px-2" aria-label={`Copiar ${etiqueta}`}><Copy size={14} /></button>
      </div>
    </div>
  );
}
